// frontend/src/components/asset/TrafficOffenceDetailModal.js
/**
 * Custodian drill-down and action surface for a single traffic offence notice.
 *
 * Shared by:
 *   - AssetAdminFleetOffences (fleet-wide deadline queue)
 *   - AssetDetailModal → Incidents & Offences tab (per-vehicle history)
 *
 * The action panel is driven by notice.status, so the custodian only ever sees
 * the one move that is actually available to them. Two deadlines are surfaced
 * separately because they have different consequences: missing the declaration
 * deadline is itself an offence, while missing the payment deadline escalates
 * to a court summons.
 *
 * Theme: Admin (red).
 */

import React, { useState } from 'react';
import {
  AlertTriangle, Car, CalendarClock, CircleDollarSign, FileText,
  MapPin, ShieldAlert, User, Eye, Send, CheckCircle2, XCircle, History,
} from 'lucide-react';
import Modal from '../UI/Modal';
import { StatusBadge } from '../UI/StatusBadge';
import SecureFileLink from './SecureFileLink';
import { formatDate, formatDateTime, formatDaySpan } from '../../utils/dateUtils';
import {
  useTrafficOffenceDetail,
  useOffenceParticulars,
  useMarkOffenceSubmitted,
  useVerifyOffencePayment,
  useCancelTrafficOffence,
} from '../../hooks/useAssetInventoryData';

// `closed` is globally a warning-tier status; a resolved offence is a success.
const badgeStatusFor = (status) => (status === 'closed' ? 'offence_closed' : status);

const CONFIDENCE_STYLES = {
  high: 'bg-green-100 text-green-700 ring-green-600/20',
  medium: 'bg-amber-100 text-amber-700 ring-amber-600/20',
  low: 'bg-red-100 text-red-700 ring-red-600/20',
  none: 'bg-gray-100 text-gray-700 ring-gray-600/20',
};

const DetailRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2 min-w-0">
    <Icon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
    <div className="min-w-0">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm text-gray-900 break-words">{value || '—'}</p>
    </div>
  </div>
);

/** Countdown banner. Colour tracks urgency, not status. */
const DeadlineBanner = ({ label, days, date }) => {
  if (days === null || days === undefined) return null;

  const overdue = days < 0;
  const urgent = days >= 0 && days <= 3;
  const tone = overdue
    ? 'bg-red-50 border-red-200 text-red-800'
    : urgent
      ? 'bg-amber-50 border-amber-200 text-amber-800'
      : 'bg-gray-50 border-gray-200 text-gray-700';
  const text = overdue
    ? `${formatDaySpan(Math.abs(days), 'long')} overdue`
    : days === 0
      ? 'Due today'
      : `${formatDaySpan(days, 'long')} left`;

  return (
    <div className={`rounded-xl border px-3 py-2 flex items-center gap-2 ${tone}`}>
      <CalendarClock className="w-4 h-4 flex-shrink-0" />
      <span className="text-sm font-medium">{label}: {text}</span>
      <span className="text-xs opacity-75 ml-auto whitespace-nowrap">{formatDate(date)}</span>
    </div>
  );
};

/** The live who-was-driving round, with the evidence behind the suggestion. */
const DriverPanel = ({ declaration, onChangeDriver, canChange }) => {
  if (!declaration) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
        <p className="text-sm font-medium text-amber-900 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          No driver identified
        </p>
        <p className="text-xs text-amber-800 mt-1">
          Custody records could not place anyone in this vehicle. Select the driver
          manually before the declaration deadline.
        </p>
        {canChange && (
          <button type="button" onClick={onChangeDriver} className="btn btn-admin-primary mt-3">
            <User className="w-4 h-4 mr-2" />
            Select Driver
          </button>
        )}
      </div>
    );
  }

  const confidenceCls = CONFIDENCE_STYLES[declaration.suggestion_confidence]
    || CONFIDENCE_STYLES.none;

  return (
    <div className="rounded-xl border border-gray-200 p-3 space-y-2">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">{declaration.driver_name}</p>
          <p className="text-xs text-gray-500">{declaration.driver_staff_id}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${confidenceCls}`}
          >
            {declaration.confidence_display} confidence
          </span>
          <StatusBadge
            status={declaration.response === 'confirmed' ? 'confirmed'
              : declaration.response === 'disputed' ? 'disputed' : 'pending'}
            label={declaration.response_display}
          />
        </div>
      </div>

      {declaration.suggestion_basis && (
        <p className="text-xs text-gray-600 bg-gray-50 rounded-lg px-2.5 py-2">
          {declaration.suggestion_basis}
        </p>
      )}

      {declaration.response === 'disputed' && declaration.dispute_reason && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-2.5 py-2">
          <p className="text-xs font-medium text-red-900">Driver&apos;s dispute</p>
          <p className="text-xs text-red-800 mt-0.5">&ldquo;{declaration.dispute_reason}&rdquo;</p>
        </div>
      )}

      {canChange && (
        <button type="button" onClick={onChangeDriver} className="btn btn-admin-outline">
          <User className="w-4 h-4 mr-2" />
          {declaration.response === 'disputed' ? 'Propose Different Driver' : 'Change Driver'}
        </button>
      )}
    </div>
  );
};

/** Particulars for the offline declaration. Opened on demand — every view is audited. */
const ParticularsPanel = ({ noticeId }) => {
  const [revealed, setRevealed] = useState(false);
  const { data, isLoading, error } = useOffenceParticulars(noticeId, revealed);

  if (!revealed) {
    return (
      <button type="button" onClick={() => setRevealed(true)} className="btn btn-admin-outline">
        <Eye className="w-4 h-4 mr-2" />
        Show Driver Particulars
      </button>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-3">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600" />
        Loading particulars...
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
        {error?.response?.data?.error || 'Could not load particulars.'}
      </p>
    );
  }

  const rows = [
    ['Full name', data?.full_name],
    ['NRIC / FIN', data?.nric_fin],
    ['Address', data?.address],
    ['Postal code', data?.postal_code],
    ['Contact', data?.contact_number],
    ['Licence no.', data?.licence_number],
    ['Licence class', (data?.licence_classes || []).join(', ')],
    ['Licence expiry', data?.licence_expiry ? formatDate(data.licence_expiry) : null],
  ];

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-2">
      <p className="text-xs text-gray-500">
        Viewing this data is recorded against your name in the PDPA audit log.
      </p>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
        {rows.map(([label, value]) => (
          <div key={label} className="min-w-0">
            <dt className="text-xs text-gray-500">{label}</dt>
            <dd className="text-sm text-gray-900 break-words">{value || '—'}</dd>
          </div>
        ))}
      </dl>
      {(data?.missing || []).length > 0 && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2">
          Missing from the employee&apos;s HR record: {data.missing.join(', ')}. Obtain these
          before submitting the declaration.
        </p>
      )}
    </div>
  );
};

/** Superseded rounds — kept as evidence of who was proposed and why they were ruled out. */
const DeclarationHistory = ({ declarations }) => {
  const past = (declarations || []).filter((d) => !d.is_current);
  if (past.length === 0) return null;

  return (
    <details className="rounded-xl border border-gray-200">
      <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-gray-700 flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded-xl">
        <History className="w-4 h-4 text-gray-400" />
        Earlier driver proposals ({past.length})
      </summary>
      <ul className="border-t border-gray-100 divide-y divide-gray-100">
        {past.map((d) => (
          <li key={d.id} className="px-3 py-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-sm text-gray-900">{d.driver_name}</span>
              <StatusBadge
                status={d.response === 'disputed' ? 'disputed' : 'superseded'}
                label={d.response === 'disputed' ? 'Disputed' : 'Superseded'}
              />
            </div>
            {d.dispute_reason && (
              <p className="text-xs text-gray-500 mt-0.5">&ldquo;{d.dispute_reason}&rdquo;</p>
            )}
            <p className="text-xs text-gray-400 mt-0.5">
              Proposed {formatDate(d.proposed_at)}
              {d.proposed_by_name && ` by ${d.proposed_by_name}`}
            </p>
          </li>
        ))}
      </ul>
    </details>
  );
};

const TrafficOffenceDetailModal = ({
  isOpen,
  onClose,
  noticeId,
  onViewVehicle,
  onChangeDriver,
  onSetDeadline,
}) => {
  const { data: notice, isLoading } = useTrafficOffenceDetail(noticeId, {
    enabled: isOpen && !!noticeId,
  });
  const [reference, setReference] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const markSubmitted = useMarkOffenceSubmitted();
  const verifyPayment = useVerifyOffencePayment();
  const cancelNotice = useCancelTrafficOffence();

  const handleClose = () => {
    setReference('');
    setCancelReason('');
    setCancelling(false);
    onClose();
  };

  const status = notice?.status;
  const isOpenNotice = status && !['closed', 'cancelled'].includes(status);
  const isCompany = !!notice?.is_company_offence;
  // Reassignment is only offered where a host supplied a handler — the
  // per-vehicle history tab is read-only, the fleet queue is where you act.
  const canChangeDriver = isOpenNotice
    && !!onChangeDriver
    && !isCompany
    && ['pending_driver_confirmation', 'disputed'].includes(status);
  const needsDeadline = isOpenNotice
    && !isCompany
    && !notice?.declaration_deadline
    && ['pending_driver_confirmation', 'disputed', 'pending_declaration'].includes(status);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Traffic Offence Notice"
      size="lg"
      theme="admin"
      contentClassName="space-y-3"
      footer={
        <>
          {notice?.asset_id && onViewVehicle && (
            <button
              type="button"
              onClick={() => onViewVehicle(notice.asset_id)}
              className="btn btn-admin-outline"
            >
              <Car className="w-4 h-4 mr-2" />
              View Vehicle
            </button>
          )}
          <button type="button" onClick={handleClose} className="btn btn-admin-outline">
            Close
          </button>
        </>
      }
    >
      {isLoading || !notice ? (
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600" />
        </div>
      ) : (
        <>
          {/* Identity */}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <ShieldAlert className="w-4 h-4 text-red-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-sm font-semibold text-gray-900 truncate">
                  {notice.notice_number}
                </h2>
                <StatusBadge
                  status={badgeStatusFor(notice.status)}
                  label={notice.status_display}
                />
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                {notice.issuing_authority_display} · {notice.offence_type_display}
                {isCompany ? ' · Company offence' : ''}
                {notice.ops_status ? ` · Ops: ${notice.ops_status.replace(/_/g, ' ')}` : ''}
              </p>
            </div>
          </div>

          {/* Deadlines */}
          {isOpenNotice && (
            <div className="space-y-2">
              {needsDeadline && onSetDeadline && (
                <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
                  <p className="text-xs text-amber-900">
                    Driver declaration deadline not set — enter it separately if required.
                  </p>
                  <button
                    type="button"
                    onClick={() => onSetDeadline(notice)}
                    className="btn btn-admin-primary flex-shrink-0 text-xs px-3 py-1.5"
                  >
                    Set Deadline
                  </button>
                </div>
              )}
              {['pending_driver_confirmation', 'disputed', 'pending_declaration'].includes(status)
                && notice.declaration_deadline && (
                <DeadlineBanner
                  label="Declaration"
                  days={notice.days_to_declaration_deadline}
                  date={notice.declaration_deadline}
                />
              )}
              {['pending_driver_confirmation', 'disputed', 'pending_declaration'].includes(status)
                && !notice.declaration_deadline && notice.payment_deadline && (
                <DeadlineBanner
                  label="Payment"
                  days={notice.days_to_payment_deadline}
                  date={notice.payment_deadline}
                />
              )}
              {status === 'pending_payment' && (
                <DeadlineBanner
                  label="Payment"
                  days={notice.days_to_payment_deadline}
                  date={notice.payment_deadline}
                />
              )}
            </div>
          )}

          {/* Facts — Ops-owned fields are read-only (.field-display) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-gray-50 rounded-xl p-3">
            <DetailRow icon={Car} label="Vehicle" value={notice.vehicle_number_raw} />
            <DetailRow
              icon={CalendarClock}
              label="Offence date & time"
              value={formatDateTime(notice.offence_datetime)}
            />
            <DetailRow icon={MapPin} label="Location" value={notice.offence_location} />
            <DetailRow
              icon={CircleDollarSign}
              label="Fine"
              value={`$${Number(notice.fine_amount || 0).toFixed(2)}`}
            />
            <DetailRow
              icon={FileText}
              label="Notice received"
              value={formatDate(notice.notice_received_date)}
            />
            {notice.ops_synced_at && (
              <DetailRow
                icon={FileText}
                label="Synced from Ops"
                value={formatDateTime(notice.ops_synced_at)}
              />
            )}
          </div>

          {/* Driver */}
          {!isCompany && (
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Driver
              </h3>
              <DriverPanel
                declaration={(notice.declarations || []).find((d) => d.is_current)}
                canChange={canChangeDriver}
                onChangeDriver={() => onChangeDriver(notice)}
              />
              <DeclarationHistory declarations={notice.declarations} />
            </div>
          )}

          {/* Action: furnish particulars offline, then record it */}
          {status === 'pending_declaration' && (
            <div className="space-y-2 rounded-xl border border-red-200 bg-red-50/50 p-3">
              <h3 className="text-sm font-semibold text-gray-900">
                Furnish particulars to {notice.issuing_authority_display}
              </h3>
              {!notice.declaration_deadline ? (
                <p className="text-xs text-amber-800">
                  Set the declaration deadline printed on the notice before recording
                  that particulars were furnished.
                </p>
              ) : (
                <>
                  <p className="text-xs text-gray-600">
                    Submit on the authority&apos;s own portal with Singpass, then record the
                    acknowledgement reference here.
                  </p>
                  <ParticularsPanel noticeId={notice.id} />
                  <div className="flex flex-col sm:flex-row gap-2 pt-1">
                    <input
                      type="text"
                      value={reference}
                      onChange={(e) => setReference(e.target.value)}
                      placeholder="Acknowledgement reference (optional)"
                      className="input-admin flex-1"
                    />
                    <button
                      type="button"
                      disabled={markSubmitted.isPending}
                      onClick={() => markSubmitted.mutate(
                        { id: notice.id, reference },
                        { onSuccess: () => setReference('') },
                      )}
                      className="btn btn-admin-primary"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Mark as Submitted
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Action: verify the receipt */}
          {status === 'pending_payment' && (
            <div className="space-y-2 rounded-xl border border-gray-200 p-3">
              <h3 className="text-sm font-semibold text-gray-900">Payment</h3>
              {notice.has_payment_proof ? (
                <>
                  <p className="text-xs text-gray-600">
                    Receipt uploaded {formatDate(notice.payment_proof_uploaded_at)}
                    {notice.payment_reference && ` · Ref ${notice.payment_reference}`}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <SecureFileLink
                      url={`/asset-inventory/traffic-offences/${notice.id}/receipt/`}
                      label="View Receipt"
                    />
                    <button
                      type="button"
                      disabled={verifyPayment.isPending}
                      onClick={() => verifyPayment.mutate({ id: notice.id })}
                      className="btn btn-admin-primary"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Verify &amp; Close
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-xs text-gray-600">
                  {isCompany
                    ? 'Company offence — pay the authority, then upload the receipt.'
                    : 'Waiting for the driver to pay and upload their receipt.'}
                </p>
              )}
            </div>
          )}

          {/* Closed summary */}
          {status === 'closed' && (
            <div className="rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-900">
              Closed — payment verified
              {notice.payment_verified_by_name && ` by ${notice.payment_verified_by_name}`}
              {notice.payment_verified_at && ` on ${formatDate(notice.payment_verified_at)}`}.
            </div>
          )}

          {/* Offline submission record */}
          {notice.declaration_submitted_at && (
            <p className="text-xs text-gray-500">
              Particulars furnished {formatDate(notice.declaration_submitted_at)}
              {notice.declaration_submitted_by_name && ` by ${notice.declaration_submitted_by_name}`}
              {notice.declaration_reference && ` · Ref ${notice.declaration_reference}`}
            </p>
          )}

          {/* Documents — only when a scan is on file (endpoint re-fetches from Ops if media is missing locally). */}
          {notice.has_notice_document && (
            <div className="flex flex-wrap gap-2">
              <SecureFileLink
                url={`/asset-inventory/traffic-offences/${notice.id}/document/`}
                label="View Notice"
              />
            </div>
          )}

          {notice.remarks && (
            <div className="rounded-xl bg-gray-50 p-3">
              <p className="text-xs text-gray-500 mb-1">Remarks</p>
              <p className="text-sm text-gray-700 whitespace-pre-line">{notice.remarks}</p>
            </div>
          )}

          {/* Cancel — appeal allowed, duplicate, or wrongly issued */}
          {isOpenNotice && (
            cancelling ? (
              <div className="rounded-xl border border-gray-200 p-3 space-y-2">
                <label htmlFor="offence-cancel-reason" className="text-sm font-medium text-gray-900">
                  Why is this notice being cancelled?
                </label>
                <input
                  id="offence-cancel-reason"
                  type="text"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="e.g. Appeal allowed by LTA"
                  className="input-admin w-full"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={!cancelReason.trim() || cancelNotice.isPending}
                    onClick={() => cancelNotice.mutate(
                      { id: notice.id, reason: cancelReason },
                      { onSuccess: () => { setCancelling(false); setCancelReason(''); } },
                    )}
                    className="btn btn-admin-primary"
                  >
                    Confirm Cancellation
                  </button>
                  <button
                    type="button"
                    onClick={() => setCancelling(false)}
                    className="btn btn-admin-outline"
                  >
                    Keep Notice
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setCancelling(true)}
                className="text-xs text-gray-500 hover:text-red-600 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded inline-flex items-center gap-1"
              >
                <XCircle className="w-3 h-3" />
                Cancel this notice
              </button>
            )
          )}
        </>
      )}
    </Modal>
  );
};

export default TrafficOffenceDetailModal;
